import { Reducer } from 'react';

import { AnyAction, combineReducers, configureStore } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';

import { apiSlice } from 'state/apiSlice';
import { userAdminApiSlice, userAdminSlice } from 'state/userAdminSlices';
// import { AnyAction } from 'redux';
import userReducer from 'state/userSlice';
import workflowReducer from 'state/workflowSlice';

import { persistReducer, persistStore } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage';

import { bookmarkApiSlice, bookmarkSlice } from './bookmarkSlices';
import { detourApiSlice, detourSlice } from './detourSlices';
import {
  originDestinationApiSlice,
  originDestinationSlice,
} from './originDestinationSlices';
import { emailAlertsApiSlice } from './emailAlertsSlices';
import { projectApiSlice } from './projectSlices';
import { realtimeApiSlice, realtimeSlice } from './realtimeSlices';
import { routeMonitoringSlice } from './routeMonitoringSlice';
import { savedRouteApiSlice, savedRouteSlice } from './savedRouteSlices';

// https://blog.logrocket.com/persist-state-redux-persist-redux-toolkit-react/

const persistConfig = {
  key: 'root',
  storage,
  stateReconciler: autoMergeLevel2,
  whitelist: ['workflow', userAdminSlice.name],
};

const rootReducer = combineReducers({
  user: userReducer,
  workflow: workflowReducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
  [bookmarkApiSlice.reducerPath]: bookmarkApiSlice.reducer,
  [bookmarkSlice.name]: bookmarkSlice.reducer,
  [savedRouteApiSlice.reducerPath]: savedRouteApiSlice.reducer,
  [savedRouteSlice.name]: savedRouteSlice.reducer,
  [realtimeApiSlice.reducerPath]: realtimeApiSlice.reducer,
  [realtimeSlice.name]: realtimeSlice.reducer,
  [detourApiSlice.reducerPath]: detourApiSlice.reducer,
  [detourSlice.name]: detourSlice.reducer,
  [routeMonitoringSlice.name]: routeMonitoringSlice.reducer,
  [userAdminApiSlice.reducerPath]: userAdminApiSlice.reducer,
  [userAdminSlice.name]: userAdminSlice.reducer,
  [originDestinationApiSlice.reducerPath]: originDestinationApiSlice.reducer,
  [originDestinationSlice.name]: originDestinationSlice.reducer,
  [emailAlertsApiSlice.reducerPath]: emailAlertsApiSlice.reducer,
  [projectApiSlice.reducerPath]: projectApiSlice.reducer,
});

const persistedReducer = persistReducer(
  persistConfig,
  rootReducer as Reducer<unknown, AnyAction>,
);

const trueRootReducer = (state, action) => {
  if (action.type === 'RESET') {
    state = undefined;
  }
  return persistedReducer(state, action);
};

//  @ts-ignore
export const store = configureStore({
  reducer: trueRootReducer,
  devTools: import.meta.env.MODE !== 'production',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        warnAfter: 256,
        ignoredActions: ['persist/PERSIST'],
      },
      immutableCheck: { warnAfter: 128 },
    })
      .concat(apiSlice.middleware)
      .concat(bookmarkApiSlice.middleware)
      .concat(realtimeApiSlice.middleware)
      .concat(savedRouteApiSlice.middleware)
      .concat(detourApiSlice.middleware)
      .concat(userAdminApiSlice.middleware)
      .concat(emailAlertsApiSlice.middleware)
      .concat(projectApiSlice.middleware)
      .concat(originDestinationApiSlice.middleware) as any,
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

export function getState(): RootState {
  return store.getState() as any as RootState;
}
export const isFetchBaseQueryErrorType = (
  error: any,
): error is FetchBaseQueryError => 'status' in error;
